import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, RadioGroup, toast } from '@/components/ui';
import type { AdminClaimProcessRequest, ClaimReason, ClaimStatus } from '@/lib/api-admin/types';
import { ApiClientError } from '@/lib/http-core';
import { isStatusEligible } from '../lib/claimActionRbac';
import { validateClaimAction } from '../schemas/adminClaimProcessSchema';
import { useClaimActionMutation } from '../hooks/useClaimActionMutation';

export type ClaimBulkAction = 'approve' | 'reject';

export interface ClaimBulkTarget {
  claimId: string;
  orderNo: string | null;
  claimStatus: ClaimStatus | null;
}

interface Failure {
  claimId: string;
  orderNo: string | null;
  message: string;
}

const REJECT_REASON_OPTIONS: ReadonlyArray<ClaimReason> = ['DEFECTIVE', 'WRONG_DELIVERY', 'MIND_CHANGE', 'SHIPPING_DELAY', 'OTHER'];

/**
 * QA-530 클레임 일괄 승인·반려 — 상세의 [승인]/[반려]와 같은 API(`/claims/{id}/approve|reject`)를 고른 클레임마다 차례로 부른다.
 * 반려는 상세처럼 사유 코드·운영 메모가 필수, 승인은 메모만(환불 금액 조정은 클레임마다 달라 상세에서만).
 * 접수·검토 중이 아닌 클레임은 부르지 않고 건너뛰며, 실패한 클레임은 주문번호와 사유를 남겨 보여 준다.
 */
export const ClaimBulkActionDialog = ({
  action,
  targets,
  onClose,
}: {
  action: ClaimBulkAction | null;
  targets: ReadonlyArray<ClaimBulkTarget>;
  /** done — 하나라도 처리했으면 true(선택 해제용). */
  onClose: (done: boolean) => void;
}) => {
  const { t } = useTranslation('claims');
  const mutation = useClaimActionMutation();
  const [note, setNote] = useState('');
  const [reasonCode, setReasonCode] = useState<ClaimReason>('DEFECTIVE');
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ done: number; failures: Failure[] } | null>(null);

  useEffect(() => {
    if (action) return;
    setNote('');
    setReasonCode('DEFECTIVE');
    setError(null);
    setProgress(0);
    setResult(null);
  }, [action]);

  if (!action) return null;
  const label = action === 'approve' ? t('claims.actions.approve', '승인') : t('claims.actions.reject', '반려');
  const eligible = targets.filter((c) => isStatusEligible(action, c.claimStatus));
  const ineligibleCount = targets.length - eligible.length;

  const run = async () => {
    const values = { note: note.trim(), reasonCode: action === 'reject' ? reasonCode : '', refundOverrideAmount: undefined };
    const check = validateClaimAction(action, values);
    if (!check.ok) {
      setError(t(`claims.errors.${check.code}`, '반려 사유 코드와 운영 메모를 입력하세요.'));
      return;
    }
    setError(null);
    const body: AdminClaimProcessRequest = {};
    if (note.trim()) body.note = note.trim();
    if (action === 'reject') body.reasonCode = reasonCode;

    setRunning(true);
    let done = 0;
    const failures: Failure[] = targets
      .filter((c) => !isStatusEligible(action, c.claimStatus))
      .map((c) => ({ claimId: c.claimId, orderNo: c.orderNo, message: t('claims.bulk.ineligible', '처리할 수 없는 상태입니다') }));
    for (const c of eligible) {
      try {
        await mutation.mutateAsync({ claimId: c.claimId, action, body });
        done++;
      } catch (e) {
        failures.push({
          claimId: c.claimId,
          orderNo: c.orderNo,
          message: e instanceof ApiClientError ? e.message : t('claims.actionModal.unknownError', '처리하지 못했습니다'),
        });
      }
      setProgress((p) => p + 1);
    }
    setRunning(false);
    setResult({ done, failures });
    const message = t('claims.bulk.result', {
      action: label,
      done,
      skipped: failures.length,
      defaultValue: '{{action}}: {{done}}건 완료, {{skipped}}건 제외',
    });
    if (failures.length === 0) {
      toast.success(message);
      onClose(true);
    } else if (done > 0) toast.warning(message);
    else toast.error(message);
  };

  return (
    <Dialog open onOpenChange={(o) => (!o && !running ? onClose(Boolean(result && result.done > 0)) : undefined)}>
      <DialogContent className="max-w-md" data-testid="claims-bulk-dialog" data-claim-action={action}>
        <DialogTitle>{t('claims.bulk.title', { action: label, defaultValue: '일괄 {{action}}' })}</DialogTitle>
        <DialogDescription>
          {t('claims.bulk.subject', {
            count: targets.length,
            eligible: eligible.length,
            defaultValue: '고른 클레임 {{count}}건 중 처리할 수 있는 {{eligible}}건을 처리합니다.',
          })}
          {ineligibleCount > 0
            ? ` ${t('claims.bulk.ineligibleNote', { count: ineligibleCount, defaultValue: '(접수·검토 중이 아닌 {{count}}건은 건너뜁니다)' })}`
            : ''}
        </DialogDescription>

        {result ? (
          <div className="mt-3 flex flex-col gap-2 text-sm" data-testid="claims-bulk-result">
            <p>
              {t('claims.bulk.result', {
                action: label,
                done: result.done,
                skipped: result.failures.length,
                defaultValue: '{{action}}: {{done}}건 완료, {{skipped}}건 제외',
              })}
            </p>
            {result.failures.length > 0 ? (
              <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto text-[12px]" style={{ color: 'var(--status-danger-fg)' }} data-testid="claims-bulk-failures">
                {result.failures.map((f) => (
                  <li key={f.claimId}>
                    {f.orderNo ?? f.claimId.slice(0, 8)}: {f.message}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="flex justify-end pt-2">
              <Button type="button" variant="outline" size="md" onClick={() => onClose(result.done > 0)}>
                {t('claims.bulk.close', '닫기')}
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="mt-4 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void run();
            }}
          >
            {action === 'approve' ? (
              <div className="rounded-md p-3 text-xs" style={{ background: 'var(--status-info-bg)', color: 'var(--status-info-fg)' }} role="note">
                {t('claims.actionModal.approveAutoRefundNotice')}
                {' '}
                {t('claims.bulk.approveAmountNote', '환불 금액은 클레임마다 예상 환불액으로 처리됩니다. 금액을 바꾸려면 상세에서 승인하세요.')}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <span id="claims-bulk-reason-label" className="text-sm text-[color:var(--content-muted)]">
                  {t('claims.actionModal.rejectReasonCode', '반려 사유 코드')}
                  <span aria-hidden="true" className="ml-1 text-[color:var(--status-danger-fg)]">*</span>
                </span>
                <RadioGroup
                  value={reasonCode}
                  onValueChange={(v) => setReasonCode(v as ClaimReason)}
                  options={REJECT_REASON_OPTIONS.map((r) => ({ value: r, label: t(`claims.reasonCode.${r}`) }))}
                  orientation="horizontal"
                  aria-labelledby="claims-bulk-reason-label"
                  data-testid="claims-bulk-reason"
                />
              </div>
            )}

            <div className="flex flex-col">
              <label htmlFor="claims-bulk-note" className="text-sm text-[color:var(--content-muted)]">
                {t('claims.actionModal.note', '운영 메모')}
                {action === 'reject' ? <span aria-hidden="true" className="ml-1 text-[color:var(--status-danger-fg)]">*</span> : null}
              </label>
              <textarea
                id="claims-bulk-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={2000}
                rows={4}
                className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                style={{ borderColor: 'var(--surface-1)' }}
                data-testid="claims-bulk-note"
              />
            </div>

            {error ? (
              <p className="text-sm" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
                {error}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-2 pt-2">
              {running ? (
                <span className="mr-auto text-xs" style={{ color: 'var(--content-muted)' }} data-testid="claims-bulk-progress">
                  {progress} / {eligible.length}
                </span>
              ) : null}
              <Button type="button" variant="outline" size="md" onClick={() => onClose(false)} disabled={running}>
                {t('claims.actions.cancel', '취소')}
              </Button>
              <Button type="submit" variant="cta" size="md" disabled={running || eligible.length === 0} data-testid="claims-bulk-submit">
                {running
                  ? t('claims.actionModal.submitting', '처리 중…')
                  : t('claims.bulk.submit', { count: eligible.length, action: label, defaultValue: '{{count}}건 {{action}}' })}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
